/**
 * Register or edit a resident (RBI Form B). Possible duplicates show while
 * typing; the privacy notice and a lawful basis are part of the form
 * (FR-RES-02, FR-RES-04).
 */
import { useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { api, ApiError } from '../../lib/api';
import { uploadFile } from '../../lib/files';
import { Alert, Button, Card, Field, PageHeader, Skeleton, inputClass } from '../../components/ui';
import { barangaySettings, CIVIL, LAWFUL, RELATIONSHIPS, relLabel, sectorLabel, type DuplicateMatch, type ResidentDetail } from './types';

interface HouseholdOption { id: string; code: string; purok: string; head: string | null; street: string; houseNo: string }

const EMPTY = {
  householdId: '', relationship: '', lastName: '', firstName: '', middleName: '', suffix: '', sex: '', birthDate: '', birthPlace: '', civilStatus: 'SINGLE',
  citizenship: 'Filipino', occupation: '', religion: '', contactNumber: '', email: '', philsys: '', residentSince: '', sectors: [] as string[],
  lawfulBasis: '', consentDate: '', consentFileId: '',
};

export function ResidentFormPage(): JSX.Element {
  const { id } = useParams();
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const settings = useQuery(barangaySettings);
  const existing = useQuery({ queryKey: ['resident', id], queryFn: () => api<ResidentDetail>(`/residents/${id}`), enabled: Boolean(id) });
  const [form, setForm] = useState({ ...EMPTY, householdId: params.get('household') ?? '' });
  const [hhSearch, setHhSearch] = useState('');
  const [matches, setMatches] = useState<DuplicateMatch[]>([]);
  const [confirmNew, setConfirmNew] = useState(false);
  const [uploading, setUploading] = useState(false);
  const households = useQuery({ queryKey: ['households-pick', hhSearch], queryFn: () => api<{ data: HouseholdOption[] }>(`/households?pageSize=20&q=${encodeURIComponent(hhSearch)}`) });
  const set = (patch: Partial<typeof form>) => setForm((f) => ({ ...f, ...patch }));

  useEffect(() => {
    const r = existing.data;
    if (!r) return;
    setForm({
      householdId: r.householdId ?? '', relationship: r.relationship, lastName: r.lastName, firstName: r.firstName, middleName: r.middleName, suffix: r.suffix, sex: r.sex,
      birthDate: r.birthDate, birthPlace: r.birthPlace, civilStatus: r.civilStatus, citizenship: r.citizenship, occupation: r.occupation, religion: r.religion,
      contactNumber: r.contactNumber, email: r.email, philsys: '', residentSince: r.residentSince ?? '', sectors: r.storedSectors,
      lawfulBasis: r.lawfulBasis, consentDate: r.consentDate ?? '', consentFileId: r.consentFileId ?? '',
    });
  }, [existing.data]);

  // FR-RES-04: look for the same person while the name and birth date are typed.
  useEffect(() => {
    if (!form.lastName || !form.firstName || (!form.birthDate && !form.philsys)) { setMatches([]); return; }
    const t = setTimeout(() => {
      void api<DuplicateMatch[]>('/residents/check-duplicates', { method: 'POST', body: { lastName: form.lastName, firstName: form.firstName, birthDate: form.birthDate || undefined, philsys: form.philsys || undefined, exceptId: id } })
        .then(setMatches).catch(() => setMatches([]));
    }, 400);
    return () => clearTimeout(t);
  }, [form.lastName, form.firstName, form.birthDate, form.philsys, id]);

  const save = useMutation({
    mutationFn: () => {
      const body: Record<string, unknown> = {
        ...form, householdId: form.householdId || null, sex: form.sex || undefined, birthDate: form.birthDate || undefined, residentSince: form.residentSince || null,
        lawfulBasis: form.lawfulBasis || undefined, consentDate: form.consentDate || null, consentFileId: form.consentFileId || null, confirmNotDuplicate: confirmNew,
        philsys: form.philsys ? form.philsys : undefined,
      };
      return id ? api<ResidentDetail>(`/residents/${id}`, { method: 'PATCH', body }) : api<ResidentDetail>('/residents', { method: 'POST', body });
    },
    onSuccess: (r) => { void qc.invalidateQueries({ queryKey: ['residents'] }); qc.setQueryData(['resident', r.id], r); navigate(`/residents/${r.id}`); },
  });
  const err = save.error instanceof ApiError ? save.error : null;
  const fe = (k: string) => err?.fields[k];
  const sectors = settings.data?.sectorTags ?? [];

  if (id && existing.isLoading) return <Skeleton className="h-96 w-full" />;
  if (existing.data?.readOnly) return <Alert tone="warning">This record is read-only ({existing.data.status.toLowerCase().replace('_', ' ')}). <Link className="underline" to={`/residents/${id}`}>Back</Link></Alert>;

  return (
    <div className="mx-auto max-w-4xl">
      <PageHeader title={id ? `Edit ${existing.data?.displayName ?? ''}` : 'New resident'} description="RBI Form B: one inhabitant. A household and its head can be set now or later." />
      {err && !Object.keys(err.fields).length && err.code !== 'DUPLICATE_SUSPECTED' && <div className="mb-4"><Alert tone="critical">{err.message}</Alert></div>}
      <form className="space-y-6" onSubmit={(e) => { e.preventDefault(); save.mutate(); }}>
        <Card title="Name and birth">
          <div className="grid gap-4 sm:grid-cols-4">
            <Field label="Last name" required error={fe('lastName')}><input className={inputClass} value={form.lastName} onChange={(e) => set({ lastName: e.target.value })} autoFocus={!id} /></Field>
            <Field label="First name" required error={fe('firstName')}><input className={inputClass} value={form.firstName} onChange={(e) => set({ firstName: e.target.value })} /></Field>
            <Field label="Middle name"><input className={inputClass} value={form.middleName} onChange={(e) => set({ middleName: e.target.value })} /></Field>
            <Field label="Suffix" hint="Jr., III"><input className={inputClass} value={form.suffix} onChange={(e) => set({ suffix: e.target.value })} /></Field>
            <Field label="Sex" required error={fe('sex')}>
              <select className={inputClass} value={form.sex} onChange={(e) => set({ sex: e.target.value })}><option value="">Choose</option><option value="MALE">Male</option><option value="FEMALE">Female</option></select>
            </Field>
            <Field label="Birth date" required error={fe('birthDate')}><input type="date" className={inputClass} value={form.birthDate} onChange={(e) => set({ birthDate: e.target.value })} /></Field>
            <Field label="Birth place"><input className={inputClass} value={form.birthPlace} onChange={(e) => set({ birthPlace: e.target.value })} /></Field>
            <Field label="Civil status">
              <select className={inputClass} value={form.civilStatus} onChange={(e) => set({ civilStatus: e.target.value })}>{Object.entries(CIVIL).map(([k, v]) => <option key={k} value={k}>{v}</option>)}</select>
            </Field>
          </div>
          {matches.length > 0 && (
            <div className="mt-4 rounded-xl border border-amber-300 bg-amber-50 p-3 text-sm dark:border-amber-800 dark:bg-amber-950">
              <p className="font-semibold text-amber-900 dark:text-amber-100">Possibly already registered</p>
              <ul className="mt-1 space-y-1">
                {matches.map((m) => <li key={m.id}><Link to={`/residents/${m.id}`} target="_blank" className="underline">{m.code} · {m.displayName}</Link> · born {m.birthDate}{m.household ? ` · ${m.household}` : ''} · <span className="text-amber-800 dark:text-amber-200">{m.reason}</span></li>)}
              </ul>
              <label className="mt-2 flex items-center gap-2"><input type="checkbox" className="h-4 w-4" checked={confirmNew} onChange={(e) => setConfirmNew(e.target.checked)} /> I checked: this is a different person</label>
            </div>
          )}
        </Card>

        <Card title="Household">
          <div className="grid gap-4 sm:grid-cols-2">
            <Field label="Household" hint="Search by household number, street or a member's name">
              <input className={`${inputClass} mb-2`} placeholder="Search households" value={hhSearch} onChange={(e) => setHhSearch(e.target.value)} />
              <select className={inputClass} value={form.householdId} onChange={(e) => set({ householdId: e.target.value })}>
                <option value="">No household yet</option>
                {form.householdId && !households.data?.data.some((h) => h.id === form.householdId) && <option value={form.householdId}>{existing.data?.household?.code ?? 'Current household'}</option>}
                {households.data?.data.map((h) => <option key={h.id} value={h.id}>{h.code} · {h.head ?? 'no head'} · {[h.houseNo, h.street, h.purok].filter(Boolean).join(', ')}</option>)}
              </select>
            </Field>
            <Field label="Relationship to the head" error={fe('relationship')}>
              <select className={inputClass} value={form.relationship} onChange={(e) => set({ relationship: e.target.value })}>
                <option value="">—</option>{RELATIONSHIPS.map((r) => <option key={r} value={r}>{relLabel(r)}</option>)}
              </select>
            </Field>
            <Field label="Resident since" hint="When they started living in the barangay (first-time jobseekers need 6 months)" error={fe('residentSince')}><input type="date" className={inputClass} value={form.residentSince} onChange={(e) => set({ residentSince: e.target.value })} /></Field>
          </div>
          <p className="mt-2 text-sm text-slate-500">New household? <Link className="text-brand underline" to="/households?new=1">Create it first</Link>, then choose it here.</p>
        </Card>

        <Card title="Other details">
          <div className="grid gap-4 sm:grid-cols-3">
            <Field label="Citizenship"><input className={inputClass} value={form.citizenship} onChange={(e) => set({ citizenship: e.target.value })} /></Field>
            <Field label="Occupation"><input className={inputClass} value={form.occupation} onChange={(e) => set({ occupation: e.target.value })} /></Field>
            <Field label="Religion"><input className={inputClass} value={form.religion} onChange={(e) => set({ religion: e.target.value })} /></Field>
            <Field label="Contact number"><input className={inputClass} value={form.contactNumber} onChange={(e) => set({ contactNumber: e.target.value })} /></Field>
            <Field label="Email" error={fe('email')}><input type="email" className={inputClass} value={form.email} onChange={(e) => set({ email: e.target.value })} /></Field>
            <Field label="PhilSys number" hint={id && existing.data?.philsys ? `On file: ${existing.data.philsys}. Type to replace.` : '12 digits (PSN) or 16 (card number). Optional.'} error={fe('philsys')}>
              <input className={inputClass} value={form.philsys} autoComplete="off" onChange={(e) => set({ philsys: e.target.value })} />
            </Field>
          </div>
          <fieldset className="mt-4">
            <legend className="mb-2 text-sm font-medium text-slate-700 dark:text-slate-200">Sectors <span className="font-normal text-slate-500">(senior citizen, youth and minor follow from the birth date)</span></legend>
            <div className="flex flex-wrap gap-3">
              {sectors.map((s) => (
                <label key={s} className="flex items-center gap-2 text-sm"><input type="checkbox" className="h-4 w-4" checked={form.sectors.includes(s)} onChange={(e) => set({ sectors: e.target.checked ? [...form.sectors, s] : form.sectors.filter((x) => x !== s) })} /> {sectorLabel(s)}</label>
              ))}
            </div>
            {fe('sectors') && <p className="mt-1 text-xs text-red-600">{fe('sectors')}</p>}
          </fieldset>
        </Card>

        <Card title="Data privacy (RA 10173)" description={`Show or read the barangay's privacy notice (version ${settings.data?.privacyNoticeVersion ?? 'v1'}) to the resident before saving.`}>
          {settings.data?.privacyNoticeText && <div className="mb-4 max-h-40 overflow-y-auto whitespace-pre-wrap rounded-xl bg-slate-50 p-3 text-sm dark:bg-slate-800">{settings.data.privacyNoticeText}</div>}
          <div className="grid gap-4 sm:grid-cols-3">
            <Field label="Lawful basis" required error={fe('lawfulBasis')}>
              <select className={inputClass} value={form.lawfulBasis} onChange={(e) => set({ lawfulBasis: e.target.value })}>
                <option value="">Choose</option>{Object.entries(LAWFUL).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
              </select>
            </Field>
            {form.lawfulBasis === 'CONSENT' && <Field label="Consent date" hint="Blank = today"><input type="date" className={inputClass} value={form.consentDate} onChange={(e) => set({ consentDate: e.target.value })} /></Field>}
            <Field label="Signed consent / notice" hint={form.consentFileId ? 'Attached' : 'Scan or photo (optional)'}>
              <input type="file" accept="application/pdf,image/png,image/jpeg" className="block w-full text-sm" disabled={uploading}
                onChange={(e) => { const f = e.target.files?.[0]; if (!f) return; setUploading(true); void uploadFile(f, 'resident_consent').then((u) => set({ consentFileId: u.id })).catch((x: Error) => window.alert(x.message)).finally(() => setUploading(false)); }} />
            </Field>
          </div>
        </Card>

        {err?.code === 'DUPLICATE_SUSPECTED' && <Alert tone="warning">{err.message} Tick “this is a different person” above if you are sure.</Alert>}
        <div className="flex justify-end gap-2">
          <Button type="button" variant="secondary" onClick={() => navigate(id ? `/residents/${id}` : '/residents')}>Cancel</Button>
          <Button type="submit" busy={save.isPending || uploading}>{id ? 'Save' : 'Register'}</Button>
        </div>
      </form>
    </div>
  );
}
